/**
 * PURE taxa-index builder: an Apache Arrow cube -> the `{label, level, path}` entries the taxa search
 * panel and the chat `taxonEntry` zoom both resolve against.
 *
 * This module is React/DOM/Redux-free so the live workspace and the server-side chart-render sidecar can
 * share one index-building implementation; it takes the raw table + the in-scope sample ids + a grade
 * floor and returns the index. The Redux selector wraps it (memoisation + cube-status plumbing); the
 * sidecar calls it directly.
 *
 * `path` is the SAME compacted lineage (null/blank ranks dropped) that the downstream consumers expect
 * (`filterCubeByPathAndGrade`, `v.taxa.slice(0, lvl+1)`), so a resolved path prefix-matches the cube.
 */
import type { Table } from 'apache-arrow'

export interface TaxonIndexEntry {
  label: string
  level: number
  /** Full compacted lineage from the top rung down to this level (inclusive). */
  path: string[]
}

// Species labels contain spaces, so a space separator would let two distinct lineages collide on one key.
// Being the lowest code unit, NUL also makes string order on the key identical to lexicographic order on the
// path, which is what `pickLineage`'s tiebreak relies on.
const PATH_SEP = '\0'

type LineageCandidate = { path: string[]; count: number }
type IndexCandidate = { label: string; level: number; lineages: Map<string, LineageCandidate> }

/**
 * Build the taxa index from `table`, restricted to `sampleIds` and rows at/above `grade`. A taxon can sit
 * under several lineages (a homonym, or null-pattern heterogeneity); the lineage the most rows agree on wins,
 * with a lexicographic tiebreak so the index is independent of cube row order. Output is sorted by label to
 * match the search panel's expectation. Missing `sampleId`/`taxa`/`grade` columns (a cube variant without a
 * taxonomy ladder) yield `[]` rather than throwing — callers treat empty as "nothing to resolve".
 */
export function buildTaxaIndex(table: Table, sampleIds: Set<string>, grade: number): TaxonIndexEntry[] {
  const seen = new Map<string, IndexCandidate>() // `${level}:${label}`

  const sampleIdCol = table.getChild('sampleId')
  const taxaCol = table.getChild('taxa')
  const gradeCol = table.getChild('grade')
  if (!sampleIdCol || !taxaCol || !gradeCol) return []

  const numRows = table.numRows
  for (let i = 0; i < numRows; i++) {
    const sampleId = sampleIdCol.get(i) as string | null
    if (!sampleId || !sampleIds.has(sampleId)) continue
    const g = gradeCol.get(i) as number | null
    if (g === null || g < grade) continue

    const taxaRow = taxaCol.get(i) as { get(j: number): string | null } | null
    if (!taxaRow) continue

    // Compacted lineage — drop nulls/blanks (matches the legacy mapNotNull semantics).
    const lineage: string[] = []
    for (let j = 0; j < 8; j++) {
      const t = taxaRow.get(j)
      if (t !== null && t !== '') lineage.push(t)
    }

    let lineageKey = ''
    for (let lvl = 0; lvl < lineage.length; lvl++) {
      const label = lineage[lvl]!
      lineageKey = lvl === 0 ? label : `${lineageKey}${PATH_SEP}${label}`
      const key = `${lvl}:${label}`
      let candidate = seen.get(key)
      if (!candidate) {
        candidate = { label, level: lvl, lineages: new Map() }
        seen.set(key, candidate)
      }
      const lineageCandidate = candidate.lineages.get(lineageKey)
      if (lineageCandidate) lineageCandidate.count++
      else candidate.lineages.set(lineageKey, { path: lineage.slice(0, lvl + 1), count: 1 })
    }
  }

  const out: TaxonIndexEntry[] = []
  for (const candidate of seen.values()) {
    out.push({ label: candidate.label, level: candidate.level, path: pickLineage(candidate) })
  }
  out.sort((a, b) => a.label.localeCompare(b.label))
  return out
}

/**
 * A taxon can sit under several lineages (a homonym, or the null-pattern heterogeneity behind
 * `repairSelectedPath`). Most rows wins; the lexicographic tiebreak keeps the index independent of cube row
 * order.
 */
function pickLineage(candidate: IndexCandidate): string[] {
  let bestKey = ''
  let best: LineageCandidate | null = null
  for (const [lineageKey, lineage] of candidate.lineages) {
    if (!best || lineage.count > best.count || (lineage.count === best.count && lineageKey < bestKey)) {
      best = lineage
      bestKey = lineageKey
    }
  }
  return best!.path
}
